import Component from 'flarum/common/Component';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';

export default class QuizPanel extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.article = vnode.attrs.article;
    this.questions = vnode.attrs.questions || [];

    this.answers = {};
    this.submitting = false;
    this.result = null; // { total, correct, passed, results[] }
  }

  view() {
    if (!this.questions || this.questions.length === 0) return null;

    // If already submitted, show result
    if (this.result) {
      return this.renderResult();
    }

    return (
      <div className="QuizPanel">
        {this.questions.map((q, idx) => this.renderQuestion(q, idx))}
        <button
          className="Button Button--primary QuizPanel-submitBtn"
          onclick={() => this.submitQuiz()}
          disabled={this.submitting}
        >
          {this.submitting ? (
            <span><i className="fas fa-spinner fa-pulse" /> 批改中...</span>
          ) : (
            <span><i className="fas fa-check" /> 提交答案</span>
          )}
        </button>
      </div>
    );
  }

  renderQuestion(q, idx) {
    const isRadio = q.questionType() === 'choice' || q.questionType() === 'true_false';
    const isCheckbox = q.questionType() === 'multiple';
    const inputType = isRadio ? 'radio' : 'checkbox';
    const options = typeof q.options() === 'string' ? JSON.parse(q.options()) : (q.options() || []);
    const questionId = q.id();
    const selected = this.answers[questionId] || [];

    return (
      <div className="QuizPanel-question">
        <div className="QuizPanel-questionText">
          第 {idx + 1} 题：
          <span className="QuizPanel-questionType">
            {q.questionType() === 'true_false' ? '（判断题）' : isRadio ? '（单选题）' : '（多选题）'}
          </span>
        </div>
        <div style={{ fontSize: '15px', marginBottom: '12px', lineHeight: 1.6 }}>{q.question()}</div>
        <div className="QuizPanel-options">
          {options.map((opt, optIdx) => {
            const optValue = typeof opt === 'string' ? opt : (opt.value || opt.label || String(opt));
            const isSelected = isRadio
              ? selected === optValue
              : selected.includes(optValue);

            return (
              <label className="QuizPanel-option">
                <input
                  type={inputType}
                  name={'quiz-' + questionId}
                  checked={isSelected}
                  onchange={() => this.toggleAnswer(questionId, optValue, isRadio)}
                />
                {optValue}
              </label>
            );
          })}
        </div>
      </div>
    );
  }

  toggleAnswer(questionId, value, isRadio) {
    if (isRadio) {
      this.answers[questionId] = value;
    } else {
      const current = this.answers[questionId] || [];
      const idx = current.indexOf(value);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(value);
      }
      this.answers[questionId] = current;
    }
    m.redraw();
  }

  async submitQuiz() {
    // Validate - all questions must be answered
    const unanswered = this.questions.filter((q) => {
      const ans = this.answers[q.id()];
      return ans === undefined || ans === null || ans === '' || (Array.isArray(ans) && ans.length === 0);
    });

    if (unanswered.length > 0) {
      alert('请回答所有问题后再提交');
      return;
    }

    this.submitting = true;

    try {
      const apiUrl = app.forum.attribute('apiUrl');
      const res = await app.request({
        method: 'POST',
        url: `${apiUrl}/taro/cms/articles/${this.article.id()}/quiz`,
        body: {
          data: {
            attributes: {
              answers: this.answers,
            },
          },
        },
      });

      // Map results back with question index for display
      this.result = {
        total: res.total || 0,
        correct: res.correct || 0,
        passed: res.passed || false,
        results: res.results || [],
      };

      // If passed, notify parent to enable completion
      if (this.attrs.onComplete) {
        this.attrs.onComplete(this.result.passed);
      }
    } catch (e) {
      const body = e.response ? JSON.parse(e.response) : {};
      alert(body.errors?.[0]?.detail || '提交失败，请重试');
    } finally {
      this.submitting = false;
      m.redraw();
    }
  }

  renderResult() {
    const r = this.result;
    const percent = r.total > 0 ? Math.round((r.correct / r.total) * 100) : 0;

    // Build a map of questionId → result
    const resultMap = {};
    (r.results || []).forEach((res) => {
      resultMap[res.question_id] = res;
    });

    return (
      <div className="QuizPanel">
        <div className={'QuizPanel-result ' + (r.passed ? 'passed' : 'failed')}>
          <div className="QuizPanel-resultScore">
            {r.correct} / {r.total} ({percent}%)
          </div>
          <div className="QuizPanel-resultMsg">
            {r.passed ? (
              <span><i className="fas fa-check-circle" /> 恭喜，通过测验！</span>
            ) : (
              <span><i className="fas fa-times-circle" /> 未通过，需要全部答对</span>
            )}
          </div>
          <div className="QuizPanel-resultDetails" onclick={() => this.result = null}>
            <i className="fas fa-redo" /> 重新答题
          </div>
        </div>

        {/* Show detailed results per question */}
        {this.questions.map((q, idx) => {
          const res = resultMap[q.id()];
          if (!res) return null;

          const options = typeof q.options() === 'string' ? JSON.parse(q.options()) : (q.options() || []);
          const selected = this.answers[q.id()];
          const correctAnswer = res.correct_answer;

          return (
            <div className="QuizPanel-question" key={q.id()}>
              <div className="QuizPanel-questionText">
                第 {idx + 1} 题：{res.correct ? '✓' : '✗'}
              </div>
              <div style={{ fontSize: '15px', marginBottom: '12px', lineHeight: 1.6 }}>{q.question()}</div>
              <div className="QuizPanel-options">
                {options.map((opt) => {
                  const optValue = typeof opt === 'string' ? opt : (opt.value || opt.label || String(opt));
                  const isSelected = Array.isArray(selected)
                    ? selected.includes(optValue)
                    : selected === optValue;
                  const isCorrectAnswer = correctAnswer === optValue
                    || (Array.isArray(correctAnswer) && correctAnswer.includes(optValue));

                  let className = '';
                  if (isCorrectAnswer) className = 'correct';
                  else if (isSelected && !isCorrectAnswer) className = 'wrong';

                  return (
                    <label className={'QuizPanel-option ' + className}>
                      <input
                        type="checkbox"
                        checked={isSelected || isCorrectAnswer}
                        disabled
                      />
                      {optValue}
                      {isCorrectAnswer && <i className="fas fa-check" style={{ marginLeft: 'auto', color: '#22a67e' }} />}
                      {isSelected && !isCorrectAnswer && <i className="fas fa-times" style={{ marginLeft: 'auto', color: '#e8604c' }} />}
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }
}
